import React, { useMemo, useState } from 'react';
import CommonLayout from './CommonLayout';
import { useUserSessionStore } from '../../stores/UserSession.store';
import moment from 'moment';
import Label from '../../components/Label/Label';
import Input from '../../shared/Input/Input';
import { OrderStatusEnum } from '../../enums/OrderStatus.enum';
import { OrderStatus } from '../../@core/constants/OrderStatus.constant';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { uniqBy } from 'lodash';
import {
  ArrowPathIcon,
  BriefcaseIcon,
  ClipboardIcon,
  CurrencyDollarIcon,
  DocumentArrowDownIcon,
  MapPinIcon,
  UserGroupIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import TabButton from '../../components/TabButton';
import { useUsers } from '../../hooks/collections/useUsers';
import { UserTypeEnum } from '../../@core/enums/UserType.enum';
import useModal from '../../@shared/components/Modal/hooks/useModal';
import { ExportExcelForm } from '../AccountPage/ExportExcelForm/ExportExcelForm';
import { useReservations } from '../../hooks/collections/useReservations';
import { IReservation } from '../../@core/interfaces/IReservation';

enum TabEnum {
  BUSINESS = 'business',
  USERS = 'users',
}

const AccountCompanyStatistics = () => {
  const user = useUserSessionStore((state) => state.user);
  const [startDate, setStartDate] = useState<string>(moment().subtract(30, 'days').format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(moment().format('YYYY-MM-DD'));
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedService, setSelectedService] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<TabEnum>(TabEnum.BUSINESS);
  const modal = useModal();
  const { users } = useUsers({
    loadData: true,
    queries: [
      { type: 'where', field: 'role', operator: '==', value: UserTypeEnum.USER }
    ]
  });

  const { reservations } = useReservations({
    loadData: true,
    queries: [
      { type: 'where', field: 'service.company.id', operator: '==', value: user?.id }
    ]
  });

  const handleOpenExportToExcel = () => {
    const headers = [
      { name: 'Order ID', field: 'orderId', width: 25 },
      { name: 'Created At', field: 'date', width: 15 },
      { name: 'Day', field: 'day', width: 15 },
      { name: 'Hour', field: 'hour', width: 15 },
      { name: 'Service', field: 'service', width: 30 },
      { name: 'Location', field: 'location', width: 20 },
      { name: 'Status', field: 'status', width: 15 },
      { name: 'Price', field: 'price', width: 15 }
    ];

    const data = filteredReservations.map(reservation => {
      const createdAt = (reservation.createdAt as any);
      const createdDate = createdAt?.toDate ? createdAt.toDate() : createdAt;
      return ({
        orderId: reservation.orderId || '',
        date: createdDate ? moment(createdDate).format('YYYY-MM-DD') : '',
        day: reservation.day || '',
        hour: reservation.hour || '',
        service: reservation?.service?.title || '',
        location: reservation.service?.details?.location?.name || '',
        status: OrderStatus[reservation.paymentStatus]?.label || reservation.paymentStatus,
        price: reservation.service?.price ? `$${Number(reservation.service.price).toFixed(2)}` : '$0.00'
      });
    });

    modal.open({
      title: 'Export to Excel',
      content: (
        <ExportExcelForm
          data={data}
          columns={headers}
          filename={`Orders_${moment().format('YYYY-MM-DD')}`}
          onClose={modal.close}
        />
      )
    });
  };

  const filteredReservations = useMemo(() => {
    const start = moment(startDate).startOf('day');
    const end = moment(endDate).endOf('day');
    return ((reservations as IReservation[]) || [])
      .filter((reservation) => {
        const createdAt = (reservation?.createdAt as any);
        const createdDate = createdAt?.toDate ? createdAt.toDate() : createdAt;
        const m = moment(createdDate);
        return m.isSameOrAfter(start);
      })
      .filter((reservation) => {
        const createdAt = (reservation?.createdAt as any);
        const createdDate = createdAt?.toDate ? createdAt.toDate() : createdAt;
        const m = moment(createdDate);
        return m.isSameOrBefore(end);
      })
      .filter((reservation) => selectedLocation === 'all' || reservation?.service?.details?.location?.id === selectedLocation)
      .filter((reservation) => selectedService === 'all' || reservation?.service?.id === selectedService);
  }, [reservations, user?.id, startDate, endDate, selectedLocation, selectedService]);

  const locations = useMemo(() => {
    const locationsWithDetails = filteredReservations
      .map((reservation) => reservation?.service?.details?.location)
      .filter((location) => location && location.id);

    return uniqBy(locationsWithDetails, 'id');
  }, [filteredReservations, user?.id]);

  const services = useMemo(() => {
    const filteredReservations = (reservations as IReservation[] || [])
      .filter((reservation) => user?.id && reservation?.service?.company?.id === user?.id);

    const servicesWithDetails = filteredReservations
      .map((reservation) => reservation?.service)
      .filter((service) => service && service.id);

    return uniqBy(servicesWithDetails, 'id');
  }, [reservations, user?.id]);

  const totalRevenue = useMemo(() => {
    return filteredReservations
      .filter((reservation) => reservation.paymentStatus === OrderStatusEnum.PAID)
      .reduce((sum: number, reservation) => sum + (Number(reservation?.service?.price) || 0), 0);
  }, [filteredReservations]);

  const paidReservationsCount = useMemo(() => {
    return filteredReservations.filter((reservation) => reservation.paymentStatus === OrderStatusEnum.PAID).length;
  }, [filteredReservations]);

  const ordersByStatus = useMemo(() => {
    const ordersWithStatus = filteredReservations
      .filter((order) => order.paymentStatus !== undefined)
      .map((order) => ({
        status: order.paymentStatus
      }));

    const uniqueStatuses = uniqBy(
      Object.values(OrderStatusEnum).map((status) => ({
        status
      })),
      'status'
    );

    return uniqueStatuses.map(({ status }) => {
      const count = ordersWithStatus.filter(order => order.status === status).length;
      return {
        name: OrderStatus[status]?.label || status,
        value: count
      };
    }).filter(item => item.value > 0);
  }, [filteredReservations]);

  const COLORS = ['#bdc3c7', '#1abc9c', '#c0392b', '#9b59b6', '#FFD133'];
  const RATING_COLORS = ['#e74c3c', '#e67e22', '#f1c40f', '#1abc9c', '#2980b9'];

  const ordersByRating = useMemo(() => {
    const ordersWithRating = filteredReservations
      .filter((order) => order.rating !== undefined && order.rating !== null)
      .map((order) => ({
        rating: order.rating
      }));

    const ratingValues = [1, 2, 3, 4, 5];

    return ratingValues.map((rating) => {
      const count = ordersWithRating.filter(order => order.rating === rating).length;
      return {
        name: `${rating} Star${rating !== 1 ? 's' : ''}`,
        value: count
      };
    }).filter(item => item.value > 0);
  }, [filteredReservations]);

  const revenueOverTime = useMemo(() => {
    const paidOrders = filteredReservations
      .filter((order: IReservation) => order.paymentStatus === OrderStatusEnum.PAID)
      .map((order: IReservation) => {
        const createdAt = (order?.createdAt as any);
        const createdDate = createdAt?.toDate ? createdAt.toDate() : createdAt;
        return ({
          day: moment(createdDate).format('YYYY-MM-DD'),
          revenue: order?.service?.price || 0
        });
      });

    const uniqueDays = uniqBy(paidOrders, 'day').map(item => item.day);
    const sortedDays = uniqueDays.sort((a, b) => moment(a).diff(moment(b)));

    return sortedDays.map(day => {
      const dayRevenue = paidOrders
        .filter(order => order.day === day)
        .reduce((sum, order) => sum + order.revenue, 0);

      return {
        day: moment(day).format('MMM DD'),
        revenue: dayRevenue
      };
    });
  }, [filteredReservations]);

  const accountRegistrations = useMemo(() => {
    const start = moment(startDate).startOf('day');
    const end = moment(endDate).endOf('day');
    const usersFiltered = (users || [])
      .filter(user => {
        const createdAt: any = user.createdAt as any;
        const createdDate = createdAt?.toDate ? createdAt.toDate() : createdAt;
        const m = moment(createdDate);
        return m.isSameOrAfter(start);
      })
      .filter(user => {
        const createdAt: any = user.createdAt as any;
        const createdDate = createdAt?.toDate ? createdAt.toDate() : createdAt;
        const m = moment(createdDate);
        return m.isSameOrBefore(end);
      });

    const usersByRegistrationDate: Record<string, number> = {};
    usersFiltered.forEach(user => {
      const createdAt: any = user.createdAt as any;
      const createdDate = createdAt?.toDate ? createdAt.toDate() : createdAt;
      const date = moment(createdDate).format('YYYY-MM-DD');
      usersByRegistrationDate[date] = (usersByRegistrationDate[date] || 0) + 1;
    });

    return Object.keys(usersByRegistrationDate)
      .sort((a, b) => moment(a).diff(moment(b)))
      .map(date => ({
        date,
        users: usersByRegistrationDate[date],
        formattedDate: moment(date).format('MMM DD, YYYY')
      }));
  }, [users, startDate, endDate]);

  const renderFiltersBusiness = () => {
    return (
      <div className="bg-white dark:bg-neutral-800 p-6 rounded-2xl border border-neutral-200 dark:border-neutral-700 mb-10">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Filter Data</h3>
          <button
            onClick={handleOpenExportToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <DocumentArrowDownIcon className="h-5 w-5"/>
            <span>Export to Excel</span>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="relative">
            <Label className="font-medium text-gray-700 dark:text-gray-300">Start Date</Label>
            <div className="mt-1 relative rounded-md">
              <Input
                className="h-11 rounded-lg border-gray-300 dark:border-gray-600 focus:ring-indigo-500 focus:border-indigo-500 block w-full"
                type="date"
                defaultValue={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          </div>
          <div className="relative">
            <Label className="font-medium text-gray-700 dark:text-gray-300">End Date</Label>
            <div className="mt-1 relative rounded-md">
              <Input
                className="h-11 rounded-lg border-gray-300 dark:border-gray-600 focus:ring-indigo-500 focus:border-indigo-500 block w-full"
                type="date"
                defaultValue={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <div className="relative">
            <Label className="font-medium text-gray-700 dark:text-gray-300">Location</Label>
            <div className="mt-1 relative rounded-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MapPinIcon className={'size-5 text-gray-400'}/>
              </div>
              <select
                className="pl-10 h-11 rounded-lg border-gray-300 dark:border-gray-600 focus:ring-indigo-500 focus:border-indigo-500 block w-full"
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                <option value="all">All Locations</option>
                {locations.map((location: any) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="relative">
            <Label className="font-medium text-gray-700 dark:text-gray-300">Service</Label>
            <div className="mt-1 relative rounded-md">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <BriefcaseIcon className={'size-5 text-gray-400'}/>
              </div>
              <select
                className="pl-10 h-11 rounded-lg border-gray-300 dark:border-gray-600 focus:ring-indigo-500 focus:border-indigo-500 block w-full"
                value={selectedService}
                onChange={(e) => setSelectedService(e.target.value)}
              >
                <option value="all">All Services</option>
                {services.map((service: any) => (
                  <option key={service.id} value={service.id}>
                    {service.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderFiltersUsers = () => {
    return (
      <div className="bg-white dark:bg-neutral-800 p-6 rounded-2xl border border-neutral-200 dark:border-neutral-700 mb-10">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Filter Data</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="relative">
            <Label className="font-medium text-gray-700 dark:text-gray-300">Start Date</Label>
            <div className="mt-1 relative rounded-md">
              <Input
                className="h-11 rounded-lg border-gray-300 dark:border-gray-600 focus:ring-indigo-500 focus:border-indigo-500 block w-full"
                type="date"
                defaultValue={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
          </div>
          <div className="relative">
            <Label className="font-medium text-gray-700 dark:text-gray-300">End Date</Label>
            <div className="mt-1 relative rounded-md">
              <Input
                className="h-11 rounded-lg border-gray-300 dark:border-gray-600 focus:ring-indigo-500 focus:border-indigo-500 block w-full"
                type="date"
                defaultValue={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyState = ({ title, description }) => {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-lg text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">{description}</p>
        </div>
      </div>
    );
  };

  const renderUsersContent = () => {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="mb-10">
          <h2 className="text-3xl font-semibold">Users Statistics</h2>
          <p className="mt-3 text-md">
            View statistics about your users and engagement with your services.
          </p>
        </div>
        <div className="w-24 border-b-2 border-neutral-700 mb-10"></div>

        {renderFiltersUsers()}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
          <div className="p-6 rounded-3xl border dark:border-neutral-700 bg-white dark:bg-neutral-800">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Total accounts</h3>
              <UserGroupIcon className={'size-8'}/>
            </div>
            <p className="text-4xl font-bold mt-4">{users.length}</p>
          </div>
          <div className="p-6 rounded-3xl border dark:border-neutral-700 bg-white dark:bg-neutral-800">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">New Users</h3>
              <UserPlusIcon className={'size-8'}/>
            </div>
            <p className="text-4xl font-bold mt-4">{Object.values(accountRegistrations).reduce((sum, value) => sum + value.users, 0)}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 mt-10">
          <div className="bg-white dark:bg-neutral-800 p-8 rounded-3xl border border-neutral-200 dark:border-neutral-700">
            <h3 className="text-2xl font-semibold mb-6 text-center">Reservations by Status</h3>
            <div className="h-96">
              {accountRegistrations.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={accountRegistrations}
                    margin={{ top: 10, right: 30, left: 20, bottom: 40 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--grid-stroke, #f0f0f0)"
                      className="dark:[--grid-stroke:#333333]"
                    />
                    <XAxis
                      dataKey="formattedDate"
                      tick={{ fill: 'var(--axis-text, #666)', fontSize: 12 }}
                      tickLine={{ stroke: 'var(--axis-line, #ccc)' }}
                      axisLine={{ stroke: 'var(--axis-line, #ccc)' }}
                      height={60}
                      angle={-45}
                      textAnchor="end"
                      className="dark:[--axis-text:#999] dark:[--axis-line:#444]"
                    />
                    <YAxis
                      tick={{ fill: 'var(--axis-text, #666)', fontSize: 12 }}
                      tickLine={{ stroke: 'var(--axis-line, #ccc)' }}
                      axisLine={{ stroke: 'var(--axis-line, #ccc)' }}
                      className="dark:[--axis-text:#999] dark:[--axis-line:#444]"
                    />
                    <Tooltip
                      formatter={(value) => [`${value} users`, 'New Registrations']}
                      contentStyle={{
                        backgroundColor: 'var(--tooltip-bg, rgba(255, 255, 255, 0.95))',
                        color: 'var(--tooltip-text, #333)',
                        borderRadius: '8px',
                        padding: '10px',
                        border: '1px solid var(--tooltip-border, #eee)',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                      }}
                      wrapperClassName="dark:[--tooltip-bg:rgba(50,50,50,0.95)] dark:[--tooltip-text:white] dark:[--tooltip-border:#555]"
                      labelFormatter={(label) => `Date: ${label}`}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '20px' }}
                    />
                    <Line
                      type="monotone"
                      dataKey="users"
                      name="User Registrations"
                      stroke="#3498db"
                      strokeWidth={2}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      animationDuration={1500}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                renderEmptyState({
                  title: 'No User Registrations',
                  description: 'Try adjusting your filters.'
                })
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderBusinessContent = () => {
    return (
      <div className="space-y-6 sm:space-y-8">
        <div className="mb-10">
          <h2 className="text-3xl font-semibold">Revenue Statistics</h2>
          <p className="mt-3 text-md">
            View financial statistics about your reservations and track your business performance.
          </p>
        </div>
        <div className="w-24 border-b-2 border-neutral-700 mb-10"></div>

        {renderFiltersBusiness()}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
          <div className="p-6 rounded-3xl border dark:border-neutral-700 bg-white dark:bg-neutral-800">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Total Revenue</h3>
              <CurrencyDollarIcon className={'size-8'}/>
            </div>
            <p className="text-4xl font-bold mt-4">${totalRevenue.toFixed(2)}</p>
          </div>
          <div className="p-6 rounded-3xl border dark:border-neutral-700 bg-white dark:bg-neutral-800">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Total Reservations</h3>
              <ClipboardIcon className={'size-8'}/>
            </div>
            <p className="text-4xl font-bold mt-4">{filteredReservations.length}</p>
          </div>
          <div className="p-6 rounded-3xl border dark:border-neutral-700 bg-white dark:bg-neutral-800">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">Average Reservation</h3>
              <ArrowPathIcon className={'size-8'}/>
            </div>
            <p className="text-4xl font-bold mt-4">
              ${paidReservationsCount ? (totalRevenue / paidReservationsCount).toFixed(2) : '0.00'}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-10">
          <div className="bg-white dark:bg-neutral-800 p-8 rounded-3xl border border-neutral-200 dark:border-neutral-700">
            <h3 className="text-2xl font-semibold mb-6 text-center">Reservations by Status</h3>
            <div className="h-96">
              {ordersByStatus.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={ordersByStatus}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {ordersByStatus.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${value} reservations`, 'Count']}
                      contentStyle={{
                        backgroundColor: 'var(--tooltip-bg, rgba(255, 255, 255, 0.95))',
                        color: 'var(--tooltip-text, #333)',
                        borderRadius: '8px',
                        padding: '10px',
                        border: '1px solid var(--tooltip-border, #eee)',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '20px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                renderEmptyState({
                  title: 'No Reservations Found',
                  description: 'Try adjusting your filters.'
                })
              )}
            </div>
          </div>

          <div className="bg-white dark:bg-neutral-800 p-8 rounded-3xl border border-neutral-200 dark:border-neutral-700">
            <h3 className="text-2xl font-semibold mb-6 text-center">Revenue Over Time</h3>
            <div className="h-96">
              {revenueOverTime.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={revenueOverTime}
                    margin={{ top: 10, right: 30, left: 20, bottom: 40 }}
                  >
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="var(--grid-stroke, #f0f0f0)"
                      className="dark:[--grid-stroke:#333333]"
                    />
                    <XAxis
                      dataKey="day"
                      tick={{ fill: 'var(--axis-text, #666)', fontSize: 12 }}
                      tickLine={{ stroke: 'var(--axis-line, #ccc)' }}
                      axisLine={{ stroke: 'var(--axis-line, #ccc)' }}
                      height={60}
                      angle={-45}
                      textAnchor="end"
                      className="dark:[--axis-text:#999] dark:[--axis-line:#444]"
                    />
                    <YAxis
                      tick={{ fill: 'var(--axis-text, #666)', fontSize: 12 }}
                      tickLine={{ stroke: 'var(--axis-line, #ccc)' }}
                      axisLine={{ stroke: 'var(--axis-line, #ccc)' }}
                      tickFormatter={(value) => `$${value}`}
                      className="dark:[--axis-text:#999] dark:[--axis-line:#444]"
                    />
                    <Tooltip
                      formatter={(value) => [`$${value}`, 'Revenue']}
                      contentStyle={{
                        backgroundColor: 'var(--tooltip-bg, rgba(255, 255, 255, 0.95))',
                        color: 'var(--tooltip-text, #333)',
                        borderRadius: '8px',
                        padding: '10px',
                        border: '1px solid var(--tooltip-border, #eee)',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                      }}
                      wrapperClassName="dark:[--tooltip-bg:rgba(50,50,50,0.95)] dark:[--tooltip-text:white] dark:[--tooltip-border:#555]"
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '20px' }}
                    />
                    <Bar
                      dataKey="revenue"
                      fill="#3498db"
                      name="Revenue"
                      radius={[4, 4, 0, 0]}
                      barSize={40}
                      animationDuration={1500}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                renderEmptyState({
                  title: 'No Revenue Data',
                  description: 'Try adjusting your filters.'
                })
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-8 mt-10">
          <div className="bg-white dark:bg-neutral-800 p-8 rounded-3xl border border-neutral-200 dark:border-neutral-700">
            <h3 className="text-2xl font-semibold mb-6 text-center">Rating Distribution</h3>
            <div className="h-96">
              {ordersByRating.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={ordersByRating}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {ordersByRating.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={RATING_COLORS[index % RATING_COLORS.length]}/>
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value) => [`${value} reservations`, 'Count']}
                      contentStyle={{
                        backgroundColor: 'var(--tooltip-bg, rgba(255, 255, 255, 0.95))',
                        color: 'var(--tooltip-text, #333)',
                        borderRadius: '8px',
                        padding: '10px',
                        border: '1px solid var(--tooltip-border, #eee)',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Legend
                      wrapperStyle={{ paddingTop: '20px' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                renderEmptyState({
                  title: 'No Ratings Found',
                  description: 'No reservations with ratings in the selected period.'
                })
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTabNavigation = () => {
    return (
      <div className="flex border-b border-neutral-200 dark:border-neutral-700 mb-8">
        <TabButton
          label="Business"
          isActive={activeTab === TabEnum.BUSINESS}
          onClick={() => setActiveTab(TabEnum.BUSINESS)}
        />
        <TabButton
          label="Users"
          isActive={activeTab === TabEnum.USERS}
          onClick={() => setActiveTab(TabEnum.USERS)}
        />
      </div>
    );
  };

  return (
    <div>
      <CommonLayout>
        {renderTabNavigation()}
        <>{activeTab === TabEnum.BUSINESS ? renderBusinessContent() : renderUsersContent()}</>
      </CommonLayout>
    </div>
  );
};

export default AccountCompanyStatistics;
